import { memo, useCallback, useContext, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

import { BRAND_DTO_ENUMS } from 'api/repositories/brand/brand.constants';
import POLICIES from 'constants/policies';
import { getLocale, useLanguage } from '@languages/index';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { CampaignFormFieldWrapperProps } from '../formFields.types';
import { CAMPAIGN_FORM_STEP_ONE_FIELD_NAME } from '../../../campaignFormStepOne.constants';
import PeriodField from './components/periodField/periodField';
import Checkbox from 'components/checkbox';
import { isJapanService, isKoreaService } from 'constants/domains/domains';

type PeriodFieldKey =
  (typeof CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD)[keyof typeof CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD];

const PeriodFields = ({ elementIndex }: CampaignFormFieldWrapperProps) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };

  const {
    modal,
    brand,
    updateCampaignId,
    campaignFormMethods,
    checkedInspectionAgreement,
  } = useContext(CampaignFormContext);

  const [
    isSingleDayPosting,
    recruitStartsAt,
    recruitEndsAt,
    selectionStartsAt,
    selectionEndsAt,
    productDeployAt,
    postingStartsAt,
    inspectionSubmitDueDt,
    inspectionFeedbackDueDt,
    postingEndsAt,
  ] = useWatch({
    control: campaignFormMethods.control,
    name: [
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.IS_SINGLE_DAY_POSTING,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_STARTS_AT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_ENDS_AT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.SELECTION_STARTS_AT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.SELECTION_ENDS_AT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.PRODUCT_DEPLOY_AT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_STARTS_AT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_SUBMIT_DUE_DT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_FEEDBACK_DUE_DT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_ENDS_AT,
    ],
  });

  const calculatePostingStartsAtDate = (type:'AUTO', productDeployAt:string) => {
    let newPostingsStartsAt = dayjs(productDeployAt);
    if (type === 'AUTO') {
      const isSameDepartureAndDestination = brand.departureCountryCode == brand.destinationCountryCode;
      const deliveryDays = isSameDepartureAndDestination
        ? brand.businessType === BRAND_DTO_ENUMS.BUSINESS_TYPE.WHOLESALE
          ? 1
          : 2
        : 7;
      newPostingsStartsAt = newPostingsStartsAt.add(deliveryDays, 'day');
      const isSunday = newPostingsStartsAt.day() === 0;
      if (isSunday) {
        newPostingsStartsAt = newPostingsStartsAt.add(1, 'day');
      }
    }
    return newPostingsStartsAt;
  }

  const calculateInspectionSubmitDueDtDate = (type:'MIN'|'AUTO') => {
    let date = dayjs();

    const [productDeployAt] = campaignFormMethods.getValues([
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.PRODUCT_DEPLOY_AT,
    ]);

    const typeOffsetMap = {
      MIN: POLICIES.INSPECTION_SUBMIT_DUE_DT.DIFF_FROM_PRODUCT_DEPLOY_AT.MIN,
      AUTO: POLICIES.INSPECTION_SUBMIT_DUE_DT.DIFF_FROM_PRODUCT_DEPLOY_AT.AUTO,
    };

    if (productDeployAt) {
      date = dayjs(productDeployAt).add(typeOffsetMap[type], 'day');
    }

    return date;
  };

  const calculateInspectionFeedbackDueDtDate = (type:'MIN'|'AUTO') => {
    let date = dayjs();

    const typeOffsetMap = {
      MIN: POLICIES.INSPECTION_FEEDBACK_DUE_DT.DIFF_FROM_INSPECTION_SUBMIT_DUE_DT.MIN,
      AUTO: POLICIES.INSPECTION_FEEDBACK_DUE_DT.DIFF_FROM_INSPECTION_SUBMIT_DUE_DT.AUTO,
    };

    const [inspectionSubmitDueDt] = campaignFormMethods.getValues([
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_SUBMIT_DUE_DT
    ]);

    if (inspectionSubmitDueDt) {
      date = dayjs(inspectionSubmitDueDt).add(typeOffsetMap[type], 'day');
    }

    return date;
  };

  /**
   * 기본 정책:
   * - 제품출고 +2~3일(마지막 일자가 일요일 경우)에 포스팅시작
   * - 포스팅시작+9일부터 포스팅 마감 선택가능
   * - 결과적으로 출고+11일부터 선택 가능으로 기획/운영 쪽은 이해하고 있음
   * 
   * 한국-일본 정책:
   * - 제품출고 +7일에 포스팅시작
   * - 이후 같음
   * 
   * 포스팅 종료
   * - 사전검수의 경우 사전검수피드백 +4일(특정일 옵션은 2일)
   * - 사전검수 아닌 경우 포스팅시작 +9일
   * 
   * @returns 
   */
  const calculatePostingEndsAtMinDate = () => {
    const[
      isSingleDayPosting,
      inspectionFeedbackDueDt,
      postingStartsAt,
    ] = campaignFormMethods.getValues([
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.IS_SINGLE_DAY_POSTING,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_FEEDBACK_DUE_DT,
      CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_STARTS_AT,
    ]);

    // 일반 시나리오 : 포스팅 시작일 (posting_starts_at) +n일
    if (!checkedInspectionAgreement) {
      let offsetFromPostingStartsAt = 0;
      if (brand.businessType == BRAND_DTO_ENUMS.BUSINESS_TYPE.WHOLESALE) {
        offsetFromPostingStartsAt = 6;
      } else {
        if (isSingleDayPosting) {
          offsetFromPostingStartsAt = 7;
        } else {
          offsetFromPostingStartsAt = 9;
        }
      }
      return postingStartsAt ? dayjs(postingStartsAt).add(offsetFromPostingStartsAt, 'day') : dayjs();

    // 사전검수 시나리오 : 사전 검수 영상 피드백 기한 (inspection_feedback_due_dt) +n일 (개발 시점에서 동대문은 신경 안써서 케이스 없음)
    } else {
      let offsetFromInspectionFeedbackDueDt = 0;
      if (isSingleDayPosting) {
        offsetFromInspectionFeedbackDueDt = 2;
      } else {
        offsetFromInspectionFeedbackDueDt = 4;
      }
      if (inspectionFeedbackDueDt) {
        const isFeedbackDueDtThursday = dayjs(inspectionFeedbackDueDt).day() === 4;
        const isFeedbackDueDtFriday = dayjs(inspectionFeedbackDueDt).day() === 5;
        if (isFeedbackDueDtThursday) {
          offsetFromInspectionFeedbackDueDt += 2;
        } else if (isFeedbackDueDtFriday) {
          offsetFromInspectionFeedbackDueDt += 1;
        }
      }
      return inspectionFeedbackDueDt ? dayjs(inspectionFeedbackDueDt).add(offsetFromInspectionFeedbackDueDt, 'day') : dayjs();
    }
  };

  /**
   * 
   * @param date JS date object
   * @param disabledDateRanges [['2025-01-01', '2025-01-02'], ['2025-01-03', '2025-01-04'], ...]
   * @returns 
   */
  const isDisableDateRanges = (date: any, disabledDateRanges: string[][]) => {
    return disabledDateRanges.some(([start, end]) => dayjs(date).isBetween(dayjs(start), dayjs(end), 'day', '[]'));
  }

  const onChangeDate = ({ key, value }: { key: PeriodFieldKey; value: string }) => {
    const showErrorModal = (text: string) => {
      modal.showModal({ text });
      return false;
    };

    const requirePreviousDate = (previousKey: PeriodFieldKey, errorMessage: string) => {
      const previousValue = campaignFormMethods.getValues(previousKey);
      return !previousValue ? showErrorModal(errorMessage) : true;
    };

    const validationRules = {
      // 모델 모집 종료일
      [CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_ENDS_AT]: () =>
        requirePreviousDate(
          CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_STARTS_AT,
          languageTexts.campaign_form_recruit_period_required_select_starts_at,
        ),
      // 모델 선정 시작일
      [CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.SELECTION_STARTS_AT]: () =>
        requirePreviousDate(
          CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_ENDS_AT,
          languageTexts.campaign_form_recruit_period_required_select_ends_at,
        ),
      // 모델 선정 종료일
      [CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.SELECTION_ENDS_AT]: () =>
        requirePreviousDate(
          CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.SELECTION_STARTS_AT,
          languageTexts.campaign_form_selection_period_required_select_starts_at,
        ),
      // 사전 검수 영상 제출 날짜
      [CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_SUBMIT_DUE_DT]: () => {
        return true;
      },
      // 포스팅 제작 기한
      [CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_STARTS_AT]: () =>
        showErrorModal(languageTexts.create_modal_select_date),
    };

    const periodUpdates = {
      // 제품 출고일
      [CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.PRODUCT_DEPLOY_AT]: () => {
        campaignFormMethods.setValue(
          CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_STARTS_AT,
          calculatePostingStartsAtDate('AUTO', value).format('YYYY-MM-DD'),
        );

        // 사전검수 아닐 경우만 포스팅 종료일 자동 설정
        if (!checkedInspectionAgreement) {
          campaignFormMethods.setValue(
            CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_ENDS_AT,
            calculatePostingEndsAtMinDate().format('YYYY-MM-DD'),
          );
        }
      },
    }

    // validation
    if (validationRules[key] != null && !validationRules[key]()) {
      return;
    }

    // 상위 날짜 컴포넌트 값 변경 시 하위 컴포넌트 값 초기화
    const orderedKeys: PeriodFieldKey[] = Object.values(CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD);
    const currentIndex = orderedKeys.indexOf(key);
    if (currentIndex !== -1) {
      for (let i = currentIndex + 1; i < orderedKeys.length; i++) {
        campaignFormMethods.setValue(orderedKeys[i], null);
      }
    }

    // 값 설정
    campaignFormMethods.setValue(key, value);

    // 하위 컴포넌트 값 자동설정
    if (periodUpdates[key] != null) {
      periodUpdates[key]();
    }
  };

  const onChangeRecruitStartsAt = (value: string) => {
    if (dayjs(value).isSame(dayjs(), 'day')) {
      modal.showModal({
        minWidth: '400px',
        title: languageTexts.create_modal_recruit_immediately_title,
        text: languageTexts.create_modal_recruit_immediately_text,
        cancelButtonText: languageTexts.cancel,
        confirmButtonText: languageTexts.confirm,
        cancel: () => {
          return false;
        },
        confirm: () => {
          onChangeDate({
            key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_STARTS_AT,
            value,
          });
        },
      });
    } else {
      onChangeDate({
        key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_STARTS_AT,
        value,
      });
    }
  };


  const onClickCheckSingleDayPosting = () => {
    if (updateCampaignId) {
      modal.showAlertModal({
        title: languageTexts.campaign_form_cant_be_update_modal_title,
        text: languageTexts.campaign_form_cant_be_update_modal_text,
      });
      return;
    }

    campaignFormMethods.setValue('isSingleDayPosting', !isSingleDayPosting);
    campaignFormMethods.setValue(CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_ENDS_AT, null);
  };

  const renderCalendarHeader = () => {
    return null;

    if (!isSingleDayPosting)
      return null;

    return (
      <div className="p-3 w-full bg-black text-white font-bold text-center">
        <span>{`추석연휴(10/3 금요일 ~ 10/9 목요일)로 인해 일부 날짜 선택이 불가할 수 있습니다.`}</span>
      </div>
    )
  }

  const renderProductDeployCalendarHeader = () => {
    return null;
    return (
      <div className="p-3 w-full bg-black text-white font-bold text-center">
        <span>{`추석연휴(10/3 금요일 ~ 10/9 목요일)로 인해 일부 날짜 선택이 불가할 수 있습니다.`}</span>
      </div>
    )
  }

  const renderSingleDayPostingCheckbox = () => {
    return (
      <div className="flex flex-row items-center gap-3">
        <Checkbox
          text={languageTexts.campaign_form_single_day_posting_field_check}
          checked={isSingleDayPosting}
          onClick={onClickCheckSingleDayPosting}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-base font-bold">
        {`${elementIndex}. ${languageTexts.campaign_form_period_field_label}`}
      </p>
      <div className="flex flex-col gap-10">
        {/* 인플루언서 모집 기간 */}
        <PeriodField
          label={languageTexts.campaign_form_recruit_period_field_label}
          descriptionClassName={isSingleDayPosting ? 'text-[#F00]' : undefined}
          startDateCalendarHeader={renderCalendarHeader}
          endDateCalendarHeader={renderCalendarHeader}
          startDate={recruitStartsAt || ''}
          endDate={recruitEndsAt || ''}
          minStartDate={dayjs().add(isJapanService() ? 1 : 0, 'day').toDate()}
          minEndDate={dayjs(recruitStartsAt).add(1, 'day').toDate()}
          onChangeStartDate={onChangeRecruitStartsAt}
          onChangeEndDate={(value) =>
            onChangeDate({
              key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.RECRUIT_ENDS_AT,
              value,
            })
          }
        />

        {/* 인플루언서 선정 기간 */}
        <PeriodField
          label={languageTexts.campaign_form_selection_period_field_label}
          startDateCalendarHeader={renderCalendarHeader}
          endDateCalendarHeader={renderCalendarHeader}
          startDate={selectionStartsAt || ''}
          endDate={selectionEndsAt || ''}
          minStartDate={recruitEndsAt ? dayjs(recruitEndsAt).add(1, 'day').toDate() : undefined}
          minEndDate={selectionStartsAt ? dayjs(selectionStartsAt).toDate() : dayjs().add(1, 'day').toDate()}
          onChangeStartDate={(value) => {
            onChangeDate({
              key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.SELECTION_STARTS_AT,
              value,
            });
          }}
          onChangeEndDate={(value) => {
            onChangeDate({
              key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.SELECTION_ENDS_AT,
              value,
            });
          }}
        />

        {/* 제품 출고 날짜 */}
        <PeriodField
          label={languageTexts.campaign_form_product_deploy_period_field_label}
          startDateCalendarHeader={renderProductDeployCalendarHeader}
          endDateCalendarHeader={renderProductDeployCalendarHeader}
          startDate={productDeployAt || ''}
          minStartDate={selectionEndsAt ? dayjs(selectionEndsAt).toDate() : undefined}
          startDatePlaceholder={languageTexts.campaign_form_product_deploy_period_field_placeholder}
          onChangeStartDate={(value) => {
            onChangeDate({
              key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.PRODUCT_DEPLOY_AT,
              value,
            });
          }}
          tileDisabled={(date: any) => {
            // if (isKoreaService()) {
            //   return false;
            // }
            return isDisableDateRanges(date, [
              ['2025-10-01', '2025-10-09'],
            ]);
          }} 
        />

        {/* 사전 검수 영상 제출 기한 */}
        {checkedInspectionAgreement && (
          <PeriodField
            // label={languageTexts.campaign_form_inspection_period_field_label}
            label={
              <>
                <span className="text-[#F00]">{languageTexts.campaign_form_inspection_period_field_label_1}</span>
                <span>{languageTexts.campaign_form_inspection_period_field_label_2}</span>
                <span className="text-[#F00]">{languageTexts.campaign_form_inspection_period_field_label_3}</span>
                <span>{languageTexts.campaign_form_inspection_period_field_label_4}</span>
              </>
            }
            description2ClassName={isSingleDayPosting ? 'text-[#F00]' : undefined}
            startDateCalendarHeader={renderCalendarHeader}
            endDateCalendarHeader={renderCalendarHeader}
            startDate={inspectionSubmitDueDt || ''}
            minStartDate={calculateInspectionSubmitDueDtDate('MIN').toDate()}
            startDatePlaceholder={languageTexts.campaign_form_inspection_period_field_placeholder}
            // maxStartDate={calculateInspectionSubmitDueDtDate('MAX').toDate()}
            onChangeStartDate={(value) => {
              onChangeDate({
                key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_SUBMIT_DUE_DT,
                value,
              });
            }}
            //// @note 달력 특정 날짜 비활성화 로직 레퍼런스
            // tileDisabled={(date: any) => {
            //   return date.getDay() === 5 || date.getDay() === 6;
            // }}
          />
        )}

        {/* 사전 검수 영상 피드백 기한 */}
        {checkedInspectionAgreement && (
          <PeriodField
            label={languageTexts.campaign_form_inspection_feedback_period_field_label}
            description={languageTexts.campaign_form_inspection_feedback_period_field_description()}
            startDateCalendarHeader={renderCalendarHeader}
            startDate={inspectionFeedbackDueDt || ''}
            minStartDate={calculateInspectionFeedbackDueDtDate('MIN').toDate()}
            onChangeStartDate={(value) => {
              onChangeDate({
                key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_FEEDBACK_DUE_DT,
                value,
              });
            }}
          />
        )}

        {/* 포스팅 or 릴스 제작 기한 */}
        {/* // 포스팅 시작일 표기 여부 */}
        {(isSingleDayPosting || checkedInspectionAgreement) ? (
          <PeriodField
            label={languageTexts.campaign_form_posting_period_field_label(brand.categoryId, isSingleDayPosting)}
            description={languageTexts.campaign_form_posting_period_field_description(brand.categoryId)}
            startDateCalendarHeader={renderCalendarHeader}
            endDateCalendarHeader={renderCalendarHeader}
            startDate={postingEndsAt || ''}
            minStartDate={calculatePostingEndsAtMinDate().toDate()}
            startDatePlaceholder={languageTexts.campaign_form_posting_period_field_posting_ends_at_placeholder(isSingleDayPosting)}
            onChangeStartDate={(value) => {
              onChangeDate({
                key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_ENDS_AT,
                value,
              });
            }}
            checkBox={renderSingleDayPostingCheckbox}
          />
        ) : (
          <PeriodField
            label={languageTexts.campaign_form_posting_period_field_label(brand.categoryId, isSingleDayPosting)}
            description={languageTexts.campaign_form_posting_period_field_description(brand.categoryId)}
            startDateCalendarHeader={renderCalendarHeader}
            endDateCalendarHeader={renderCalendarHeader}
            startDate={
              postingStartsAt
                ? dayjs(postingStartsAt).format('YY.MM.DD') +
                ` ${dayjs(postingStartsAt).format('ddd')}${getLocale() === 'ko' ? '요일' : ''}`
                : ''
            }
            endDate={postingEndsAt || ''}
            minEndDate={calculatePostingEndsAtMinDate().toDate()}
            isReadOnlyStartDate
            onChangeEndDate={(value) => {
              onChangeDate({
                key: CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_ENDS_AT,
                value,
              });
            }}
            checkBox={renderSingleDayPostingCheckbox}
          />
        )}
      </div>
    </div>
  );
};

export default memo(PeriodFields);